import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';

function UpdateWelfare() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    // Government monthly
    govMonthlyStatus: '',
    govOrganisations: [],
    govOrgOther: '',
    govWelfareTypes: [],
    govMonthlyAmount: '',
    govStartDate: '',
    govEndDate: '',
    govStopReason: '',
    govStopReasonOther: '',
    govNeverReason: '',
    govNeverReasonOther: '',
    // NGO monthly
    ngoMonthlyStatus: '',
    ngoEntities: [],
    ngoEntityOther: '',
    ngoMonthlyAmount: '',
    ngoOrgNames: '',
    ngoStartDate: '',
    ngoEndDate: '',
    // One-time
    oneTimeReceived: '',
    oneTimeOrgTypes: [],
    oneTimeGovEntities: [],
    oneTimeGovOther: '',
    oneTimeNonGovName: '',
    oneTimeAmount: '',
    // Sanashakthi
    sanashakthiRegistered: '',
    sanashakthiYear: '',
    sanashakthiBenefitReceived: '',
    sanashakthiBenefitDetails: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message: 'Missing required parameters.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const parseArrayWithOther = (str) => {
    if (!str) return { array: [], otherValue: '' };
    const items = str
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s);
    const otherItem = items.find((item) => item.startsWith('Other:'));
    if (otherItem) {
      const otherValue = otherItem.replace('Other:', '').trim();
      const array = items
        .filter((item) => !item.startsWith('Other:'))
        .concat(['Other']);
      return { array, otherValue };
    }
    return { array: items, otherValue: '' };
  };

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getWelfareAssistanceDetails(
        recordId,
        sheetId
      );

      if (!response.success) {
        setAlert({
          show: true,
          message:
            response.message || 'Failed to load welfare assistance details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      const govOrgs = parseArrayWithOther(response.record.govOrganisations);
      const govWelfare = parseArrayWithOther(response.record.govWelfareTypes);
      const ngoEnts = parseArrayWithOther(response.record.ngoEntities);
      const oneTimeOrgs = parseArrayWithOther(response.record.oneTimeOrgTypes);
      const oneTimeGovEnts = parseArrayWithOther(
        response.record.oneTimeGovEntities
      );

      // Parse date fields
      const formatDate = (year, month) => {
        if (!year || !month) return '';
        return `${year}-${month.toString().padStart(2, '0')}`;
      };

      // Handle stop reason
      let govStopReason = response.record.govStopReason || '';
      let govStopReasonOther = '';
      if (govStopReason.startsWith('Other:')) {
        govStopReasonOther = govStopReason.replace('Other:', '').trim();
        govStopReason = 'Other';
      }

      // Handle never reason
      let govNeverReason = response.record.govNeverReason || '';
      let govNeverReasonOther = '';
      if (govNeverReason.startsWith('Other:')) {
        govNeverReasonOther = govNeverReason.replace('Other:', '').trim();
        govNeverReason = 'Other';
      }

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        govMonthlyStatus: response.record.govMonthlyStatus || '',
        govOrganisations: govOrgs.array,
        govOrgOther: govOrgs.otherValue,
        govWelfareTypes: govWelfare.array,
        govMonthlyAmount: response.record.govMonthlyAmount || '',
        govStartDate: formatDate(
          response.record.govStartYear,
          response.record.govStartMonth
        ),
        govEndDate: formatDate(
          response.record.govEndYear,
          response.record.govEndMonth
        ),
        govStopReason: govStopReason,
        govStopReasonOther: govStopReasonOther,
        govNeverReason: govNeverReason,
        govNeverReasonOther: govNeverReasonOther,
        ngoMonthlyStatus: response.record.ngoMonthlyStatus || '',
        ngoEntities: ngoEnts.array,
        ngoEntityOther: ngoEnts.otherValue,
        ngoMonthlyAmount: response.record.ngoMonthlyAmount || '',
        ngoOrgNames: response.record.ngoOrgNames || '',
        ngoStartDate: formatDate(
          response.record.ngoStartYear,
          response.record.ngoStartMonth
        ),
        ngoEndDate: formatDate(
          response.record.ngoEndYear,
          response.record.ngoEndMonth
        ),
        oneTimeReceived: response.record.oneTimeReceived || '',
        oneTimeOrgTypes: oneTimeOrgs.array,
        oneTimeGovEntities: oneTimeGovEnts.array,
        oneTimeGovOther: oneTimeGovEnts.otherValue,
        oneTimeNonGovName: response.record.oneTimeNonGovName || '',
        oneTimeAmount: response.record.oneTimeAmount || '',
        sanashakthiRegistered: response.record.sanashakthiRegistered || '',
        sanashakthiYear: response.record.sanashakthiYear || '',
        sanashakthiBenefitReceived: response.record.sanashakthiBenefitReceived || '',
        sanashakthiBenefitDetails: response.record.sanashakthiBenefitDetails || '',
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updates = { [name]: value };

    // Government monthly status changes
    if (name === 'govMonthlyStatus') {
      if (!['Currently receiving', 'Received but stopped'].includes(value)) {
        updates = {
          ...updates,
          govOrganisations: [],
          govOrgOther: '',
          govWelfareTypes: [],
          govMonthlyAmount: '',
          govStartDate: '',
          govEndDate: '',
          govStopReason: '',
          govStopReasonOther: '',
        };
      }
      if (value !== 'Received but stopped') {
        updates.govEndDate = '';
        updates.govStopReason = '';
        updates.govStopReasonOther = '';
      }
      if (value !== 'Not received') {
        updates.govNeverReason = '';
        updates.govNeverReasonOther = '';
      }
    }

    // Gov stop reason
    if (name === 'govStopReason' && value !== 'Other') {
      updates.govStopReasonOther = '';
    }

    // Gov never reason
    if (name === 'govNeverReason' && value !== 'Other') {
      updates.govNeverReasonOther = '';
    }

    // NGO monthly status changes
    if (name === 'ngoMonthlyStatus') {
      if (!['Currently receiving', 'Received but stopped'].includes(value)) {
        updates = {
          ...updates,
          ngoEntities: [],
          ngoEntityOther: '',
          ngoMonthlyAmount: '',
          ngoOrgNames: '',
          ngoStartDate: '',
          ngoEndDate: '',
        };
      }
      if (value !== 'Received but stopped') {
        updates.ngoEndDate = '';
      }
    }

    // Sanashakthi
    if (name === 'sanashakthiRegistered' && value !== 'Yes') {
      updates.sanashakthiYear = '';
      updates.sanashakthiBenefitReceived = '';
      updates.sanashakthiBenefitDetails = '';
    }
    if (name === 'sanashakthiBenefitReceived' && value !== 'Yes') {
      updates.sanashakthiBenefitDetails = '';
    }

    // One-time received
    if (name === 'oneTimeReceived' && value !== 'Yes') {
      updates = {
        ...updates,
        oneTimeOrgTypes: [],
        oneTimeGovEntities: [],
        oneTimeGovOther: '',
        oneTimeNonGovName: '',
        oneTimeAmount: '',
      };
    }

    setFormData({
      ...formData,
      ...updates,
    });
  };

  const handleCheckboxChange = (fieldName, value, checked) => {
    setFormData((prev) => {
      const currentArray = prev[fieldName];
      const newArray = checked
        ? [...currentArray, value]
        : currentArray.filter((item) => item !== value);

      const updates = { [fieldName]: newArray };

      // Clear "other" fields
      if (!checked && value === 'Other') {
        if (fieldName === 'govOrganisations') updates.govOrgOther = '';
        if (fieldName === 'ngoEntities') updates.ngoEntityOther = '';
        if (fieldName === 'oneTimeGovEntities') updates.oneTimeGovOther = '';
      }

      // Handle one-time org types
      if (fieldName === 'oneTimeOrgTypes') {
        if (!newArray.includes('Government entities')) {
          updates.oneTimeGovEntities = [];
          updates.oneTimeGovOther = '';
        }
        if (!newArray.includes('Non-governmental entities')) {
          updates.oneTimeNonGovName = '';
        }
      }

      return { ...prev, ...updates };
    });
  };

  // Conditional visibility functions
  const shouldShowGovReceiving = () =>
    ['Currently receiving', 'Received but stopped'].includes(
      formData.govMonthlyStatus
    );
  const shouldShowGovStopped = () =>
    formData.govMonthlyStatus === 'Received but stopped';
  const shouldShowGovNever = () => formData.govMonthlyStatus === 'Not received';
  const shouldShowGovOrgOther = () =>
    formData.govOrganisations.includes('Other');
  const shouldShowGovStopReasonOther = () => formData.govStopReason === 'Other';
  const shouldShowGovNeverReasonOther = () =>
    formData.govNeverReason === 'Other';

  const shouldShowNgoReceiving = () =>
    ['Currently receiving', 'Received but stopped'].includes(
      formData.ngoMonthlyStatus
    );
  const shouldShowNgoStopped = () =>
    formData.ngoMonthlyStatus === 'Received but stopped';
  const shouldShowNgoEntityOther = () => formData.ngoEntities.includes('Other');

  const shouldShowOneTime = () => formData.oneTimeReceived === 'Yes';
  const shouldShowOneTimeGov = () =>
    formData.oneTimeOrgTypes.includes('Government entities');
  const shouldShowOneTimeNonGov = () =>
    formData.oneTimeOrgTypes.includes('Non-governmental entities');
  const shouldShowOneTimeGovOther = () =>
    formData.oneTimeGovEntities.includes('Other');

  const hasChanges = () => {
    const arrayToString = (arr) => arr.sort().join(', ');

    const current = {
      ...formData,
      govOrganisations: arrayToString(formData.govOrganisations),
      govWelfareTypes: arrayToString(formData.govWelfareTypes),
      ngoEntities: arrayToString(formData.ngoEntities),
      oneTimeOrgTypes: arrayToString(formData.oneTimeOrgTypes),
      oneTimeGovEntities: arrayToString(formData.oneTimeGovEntities),
    };

    const original = {
      ...originalData,
      govOrganisations: arrayToString(originalData.govOrganisations),
      govWelfareTypes: arrayToString(originalData.govWelfareTypes),
      ngoEntities: arrayToString(originalData.ngoEntities),
      oneTimeOrgTypes: arrayToString(originalData.oneTimeOrgTypes),
      oneTimeGovEntities: arrayToString(originalData.oneTimeGovEntities),
    };

    return JSON.stringify(current) !== JSON.stringify(original);
  };

  const arrayToStringWithOther = (arr, otherValue) => {
    const filtered = arr.filter((item) => item !== 'Other');
    if (arr.includes('Other') && otherValue) {
      filtered.push(`Other: ${otherValue}`);
    }
    return filtered.join(', ');
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      // Parse dates
      const parseDate = (dateStr) => {
        if (!dateStr) return { year: '', month: '' };
        const [year, month] = dateStr.split('-');
        return { year, month };
      };

      const govStart = parseDate(formData.govStartDate);
      const govEnd = parseDate(formData.govEndDate);
      const ngoStart = parseDate(formData.ngoStartDate);
      const ngoEnd = parseDate(formData.ngoEndDate);

      // Build stop reasons with "Other:" prefix
      let govStopReasonFinal = formData.govStopReason;
      if (formData.govStopReason === 'Other' && formData.govStopReasonOther) {
        govStopReasonFinal = `Other: ${formData.govStopReasonOther}`;
      }

      let govNeverReasonFinal = formData.govNeverReason;
      if (formData.govNeverReason === 'Other' && formData.govNeverReasonOther) {
        govNeverReasonFinal = `Other: ${formData.govNeverReasonOther}`;
      }

      const updateData = {
        uniqueId: recordData.uniqueId,
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
        govMonthlyStatus: formData.govMonthlyStatus,
        govOrganisations: arrayToStringWithOther(
          formData.govOrganisations,
          formData.govOrgOther
        ),
        govWelfareTypes: formData.govWelfareTypes.join(', '),
        govMonthlyAmount: formData.govMonthlyAmount,
        govStartYear: govStart.year,
        govStartMonth: govStart.month,
        govEndYear: govEnd.year,
        govEndMonth: govEnd.month,
        govStopReason: govStopReasonFinal,
        govNeverReason: govNeverReasonFinal,
        ngoMonthlyStatus: formData.ngoMonthlyStatus,
        ngoEntities: arrayToStringWithOther(
          formData.ngoEntities,
          formData.ngoEntityOther
        ),
        ngoMonthlyAmount: formData.ngoMonthlyAmount,
        ngoOrgNames: formData.ngoOrgNames,
        ngoStartYear: ngoStart.year,
        ngoStartMonth: ngoStart.month,
        ngoEndYear: ngoEnd.year,
        ngoEndMonth: ngoEnd.month,
        oneTimeReceived: formData.oneTimeReceived,
        oneTimeOrgTypes: formData.oneTimeOrgTypes.join(', '),
        oneTimeGovEntities: arrayToStringWithOther(
          formData.oneTimeGovEntities,
          formData.oneTimeGovOther
        ),
        oneTimeNonGovName: formData.oneTimeNonGovName,
        oneTimeAmount: formData.oneTimeAmount,
        sanashakthiRegistered: formData.sanashakthiRegistered,
        sanashakthiYear: formData.sanashakthiRegistered === 'Yes' ? formData.sanashakthiYear : '',
        sanashakthiBenefitReceived: formData.sanashakthiRegistered === 'Yes' ? formData.sanashakthiBenefitReceived : '',
        sanashakthiBenefitDetails: formData.sanashakthiRegistered === 'Yes' && formData.sanashakthiBenefitReceived === 'Yes' ? formData.sanashakthiBenefitDetails : '',
      };

      const response = await AppScriptAPI.updateWelfareAssistanceDetails(
        updateData
      );

      if (response.success) {
        setAlert({
          show: true,
          message: 'Welfare assistance details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/rehabilitation/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/home/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={11}
        title="Update Welfare Assistance Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading welfare assistance details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={11}
        title="Update Welfare Assistance Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load welfare assistance details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={11}
      title="Update Welfare Assistance Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        {/* GOVERNMENT MONTHLY WELFARE */}
        <div className="section-header">
          Government Monthly Welfare Assistance
        </div>

        <Form.Group className="mb-3">
          <Form.Label>
            Monthly welfare assistance received from the government{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Currently receiving"
              name="govMonthlyStatus"
              value="Currently receiving"
              checked={formData.govMonthlyStatus === 'Currently receiving'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="Received but stopped"
              name="govMonthlyStatus"
              value="Received but stopped"
              checked={formData.govMonthlyStatus === 'Received but stopped'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="Not received"
              name="govMonthlyStatus"
              value="Not received"
              checked={formData.govMonthlyStatus === 'Not received'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>

        {shouldShowGovReceiving() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>
                Monthly welfare providing government organisation (Select all
                that apply)
              </Form.Label>
              <div className="checkbox-group">
                {legendData?.govOrganisations?.map((item, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`govOrg-${index}`}
                    label={item}
                    checked={formData.govOrganisations.includes(item)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        'govOrganisations',
                        item,
                        e.target.checked
                      )
                    }
                    disabled={!canEdit}
                  />
                ))}
              </div>
            </Form.Group>

            {shouldShowGovOrgOther() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>Please specify other organisation</Form.Label>
                <Form.Control
                  type="text"
                  name="govOrgOther"
                  value={formData.govOrgOther}
                  onChange={handleInputChange}
                  placeholder="Enter organisation name"
                  disabled={!canEdit}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Type of welfare assistance (Select all that apply)
              </Form.Label>
              <div className="checkbox-group">
                {legendData?.welfareTypes?.map((item, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`govWelfare-${index}`}
                    label={item}
                    checked={formData.govWelfareTypes.includes(item)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        'govWelfareTypes',
                        item,
                        e.target.checked
                      )
                    }
                    disabled={!canEdit}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Total amount of financial assistance received per month (Rs.)
              </Form.Label>
              <Form.Control
                type="text"
                name="govMonthlyAmount"
                value={formData.govMonthlyAmount}
                onChange={handleInputChange}
                placeholder="e.g., 5000"
                disabled={!canEdit}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Receiving from (Date)</Form.Label>
              <Form.Control
                type="month"
                name="govStartDate"
                value={formData.govStartDate}
                onChange={handleInputChange}
                disabled={!canEdit}
              />
              <Form.Text>Select year and month</Form.Text>
            </Form.Group>

            {shouldShowGovStopped() && (
              <div className="conditional-field">
                <Form.Group className="mb-3">
                  <Form.Label>End of assistance (Date)</Form.Label>
                  <Form.Control
                    type="month"
                    name="govEndDate"
                    value={formData.govEndDate}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                  />
                  <Form.Text>Select year and month</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Reason for stopped assistance</Form.Label>
                  <Form.Select
                    name="govStopReason"
                    value={formData.govStopReason}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                  >
                    <option value="">Select...</option>
                    {legendData?.stopReasons?.map((item, index) => (
                      <option key={index} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {shouldShowGovStopReasonOther() && (
                  <Form.Group className="mb-3 conditional-field">
                    <Form.Label>Please specify other reason</Form.Label>
                    <Form.Control
                      type="text"
                      name="govStopReasonOther"
                      value={formData.govStopReasonOther}
                      onChange={handleInputChange}
                      placeholder="Enter reason"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                )}
              </div>
            )}
          </div>
        )}

        {shouldShowGovNever() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>
                Reason for never receiving welfare assistance
              </Form.Label>
              <Form.Select
                name="govNeverReason"
                value={formData.govNeverReason}
                onChange={handleInputChange}
                disabled={!canEdit}
              >
                <option value="">Select...</option>
                {legendData?.neverReceivedReasons?.map((item, index) => (
                  <option key={index} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {shouldShowGovNeverReasonOther() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>Please specify other reason</Form.Label>
                <Form.Control
                  type="text"
                  name="govNeverReasonOther"
                  value={formData.govNeverReasonOther}
                  onChange={handleInputChange}
                  placeholder="Enter reason"
                  disabled={!canEdit}
                />
              </Form.Group>
            )}
          </div>
        )}

        {/* NGO MONTHLY WELFARE */}
        <div className="section-header">
          Non-Governmental Monthly Welfare Assistance
        </div>

        <Form.Group className="mb-3">
          <Form.Label>
            Monthly payment from non-governmental organisations{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Currently receiving"
              name="ngoMonthlyStatus"
              value="Currently receiving"
              checked={formData.ngoMonthlyStatus === 'Currently receiving'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="Received but stopped"
              name="ngoMonthlyStatus"
              value="Received but stopped"
              checked={formData.ngoMonthlyStatus === 'Received but stopped'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="Not received"
              name="ngoMonthlyStatus"
              value="Not received"
              checked={formData.ngoMonthlyStatus === 'Not received'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>

        {shouldShowNgoReceiving() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>
                Providing non-governmental entity (Select all that apply)
              </Form.Label>
              <div className="checkbox-group">
                {legendData?.ngoEntities?.map((item, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`ngoEntity-${index}`}
                    label={item}
                    checked={formData.ngoEntities.includes(item)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        'ngoEntities',
                        item,
                        e.target.checked
                      )
                    }
                    disabled={!canEdit}
                  />
                ))}
              </div>
            </Form.Group>

            {shouldShowNgoEntityOther() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>Please specify other entity</Form.Label>
                <Form.Control
                  type="text"
                  name="ngoEntityOther"
                  value={formData.ngoEntityOther}
                  onChange={handleInputChange}
                  placeholder="Enter entity name"
                  disabled={!canEdit}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Total amount received per month (Rs.)</Form.Label>
              <Form.Control
                type="text"
                name="ngoMonthlyAmount"
                value={formData.ngoMonthlyAmount}
                onChange={handleInputChange}
                placeholder="e.g., 3000"
                disabled={!canEdit}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Organisation name(s) that has given monthly welfare
              </Form.Label>
              <Form.Control
                type="text"
                name="ngoOrgNames"
                value={formData.ngoOrgNames}
                onChange={handleInputChange}
                placeholder="Separate multiple organisations with commas"
                disabled={!canEdit}
              />
              <Form.Text>
                If multiple organisations, separate with commas
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Receiving from (Date)</Form.Label>
              <Form.Control
                type="month"
                name="ngoStartDate"
                value={formData.ngoStartDate}
                onChange={handleInputChange}
                disabled={!canEdit}
              />
              <Form.Text>Select year and month</Form.Text>
            </Form.Group>

            {shouldShowNgoStopped() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>End of assistance (Date)</Form.Label>
                <Form.Control
                  type="month"
                  name="ngoEndDate"
                  value={formData.ngoEndDate}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Text>Select year and month</Form.Text>
              </Form.Group>
            )}
          </div>
        )}

        {/* ONE-TIME WELFARE */}
        <div className="section-header">One-Time Welfare Assistance</div>

        <Form.Group className="mb-3">
          <Form.Label>
            Have you received one-time welfare assistance?{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Yes"
              name="oneTimeReceived"
              value="Yes"
              checked={formData.oneTimeReceived === 'Yes'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="No"
              name="oneTimeReceived"
              value="No"
              checked={formData.oneTimeReceived === 'No'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>

        {shouldShowOneTime() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>
                Issued organisation type (Select all that apply)
              </Form.Label>
              <div className="checkbox-group">
                {legendData?.oneTimeOrgTypes?.map((item, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`oneTimeOrg-${index}`}
                    label={item}
                    checked={formData.oneTimeOrgTypes.includes(item)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        'oneTimeOrgTypes',
                        item,
                        e.target.checked
                      )
                    }
                    disabled={!canEdit}
                  />
                ))}
              </div>
            </Form.Group>

            {shouldShowOneTimeGov() && (
              <>
                <Form.Group className="mb-3 conditional-field">
                  <Form.Label>
                    Government entities name (Select all that apply)
                  </Form.Label>
                  <div className="checkbox-group">
                    {legendData?.govOrganisations?.map((item, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`oneTimeGov-${index}`}
                        label={item}
                        checked={formData.oneTimeGovEntities.includes(item)}
                        onChange={(e) =>
                          handleCheckboxChange(
                            'oneTimeGovEntities',
                            item,
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                  <span className="info-badge">For government entities</span>
                </Form.Group>

                {shouldShowOneTimeGovOther() && (
                  <Form.Group className="mb-3 conditional-field">
                    <Form.Label>
                      Please specify other government entity
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="oneTimeGovOther"
                      value={formData.oneTimeGovOther}
                      onChange={handleInputChange}
                      placeholder="Enter government entity name"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                )}
              </>
            )}

            {shouldShowOneTimeNonGov() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>Non-governmental entity name</Form.Label>
                <Form.Control
                  type="text"
                  name="oneTimeNonGovName"
                  value={formData.oneTimeNonGovName}
                  onChange={handleInputChange}
                  placeholder="Enter entity name"
                  disabled={!canEdit}
                />
                <span className="info-badge">
                  For non-governmental entities
                </span>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Total amount received from one-time support (Rs.)
              </Form.Label>
              <Form.Control
                type="text"
                name="oneTimeAmount"
                value={formData.oneTimeAmount}
                onChange={handleInputChange}
                placeholder="e.g., 50000"
                disabled={!canEdit}
              />
            </Form.Group>
          </div>
        )}

        {/* SANASHAKTHI SELF-HELP ASSOCIATION */}
        <div className="section-header">Swashakthi Self-Help Association</div>

        <Form.Group className="mb-3">
          <Form.Label>
            Are you registered in the Swashakthi self-help Association in the
            division?
          </Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Yes"
              name="sanashakthiRegistered"
              value="Yes"
              checked={formData.sanashakthiRegistered === 'Yes'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="No"
              name="sanashakthiRegistered"
              value="No"
              checked={formData.sanashakthiRegistered === 'No'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>

        {formData.sanashakthiRegistered === 'Yes' && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>Year of Registration</Form.Label>
              <Form.Select
                name="sanashakthiYear"
                value={formData.sanashakthiYear}
                onChange={handleInputChange}
                disabled={!canEdit}
              >
                <option value="">Select year...</option>
                {Array.from(
                  { length: new Date().getFullYear() - 1999 },
                  (_, i) => new Date().getFullYear() - i
                ).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Received any benefits from the Swashakthi association?
              </Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="sanashakthiBenefitReceived"
                  value="Yes"
                  checked={formData.sanashakthiBenefitReceived === 'Yes'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="sanashakthiBenefitReceived"
                  value="No"
                  checked={formData.sanashakthiBenefitReceived === 'No'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
              </div>
            </Form.Group>

            {formData.sanashakthiBenefitReceived === 'Yes' && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>What benefits have you received?</Form.Label>
                <Form.Control
                  type="text"
                  name="sanashakthiBenefitDetails"
                  value={formData.sanashakthiBenefitDetails}
                  onChange={handleInputChange}
                  placeholder="Describe the benefits received"
                  disabled={!canEdit}
                />
              </Form.Group>
            )}
          </div>
        )}
      </Form>
    </UpdateLayout>
  );
}

export default UpdateWelfare;
